/* eslint-disable no-restricted-imports */
import dayjs, { type UnitTypeShort } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { isSameDate, leadingZero } from '@vkontakte/vkjs';
import { AvailableLocales, DAYS_IN_MONTH, DAYS_IN_WEEK, mapLocaleToImport } from './consts';
import type { FlexDate } from './types';

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Возвращает разницу между датами в днях (float)
 */
export const diffDates = (startDate: FlexDate, endDate: FlexDate) => {
  return dayjs(startDate).diff(endDate, 'days', true);
};

/**
 * Проверяет что переданные даты находятся в одном дне
 */
export function isEqualDates(date1: FlexDate, date2: FlexDate) {
  return isSameDate(new Date(date1), new Date(date2));
}

/**
 * Возвращает разницу между датами в милисекундах
 */
export function diffMilliseconds(startDate: FlexDate, endDate: FlexDate) {
  return dayjs(startDate).diff(endDate, 'millisecond');
}

/**
 * Возвращает разницу между датами в секундах
 */
export function diffSeconds(startDate: FlexDate, endDate: FlexDate) {
  return dayjs(startDate).diff(endDate, 'second');
}

/**
 * Возвращает разницу между датами в годах
 */
export function diffYears(startDate: FlexDate, endDate: FlexDate) {
  return dayjs(startDate).diff(endDate, 'y');
}

/**
 * Возвращает разницу между датами
 * По умолчанию формат HH:mm:ss
 */
export function diffTime(startDate: FlexDate, endDate: FlexDate, timeFormat = 'HH:mm:ss') {
  const timeOffset = dayjs(endDate).diff(startDate);

  return formatOffset(timeOffset, timeFormat);
}

/**
 * Форматирует переданное количество милисекунд
 * По умолчанию формат HH:mm:ss
 */
export function formatOffset(offsetMs: number, timeFormat = 'HH:mm:ss') {
  return dayjs.duration(offsetMs).format(timeFormat);
}

/**
 * Возвращает дату в будущем
 * @param date - дата к которой нужно добавить {count} дней
 * @param count - количество дней, которое необходимо прибавить к {date}
 */
export const addDaysToDate = (date: FlexDate, count: number) => {
  return (
    dayjs(date)
      // используются hours вместо days для возможности передавать нецелое число
      .add(count * 24, 'hours')
      .toDate()
  );
};

/**
 * Возвращает дату в прошлом
 * @param date - дата из которой нужно вычесть {count} дней
 * @param count - количество дней, которое необходимо прибавить к {date}
 */
export const subtractDaysFromDate = (date: FlexDate, count: number) => {
  return (
    dayjs(date)
      // используются hours вместо days для возможности передавать нецелое число
      .subtract(count * 24, 'hours')
      .toDate()
  );
};

/**
 * Возвращает дату в будущем
 * @param date - дата к которой нужно добавить {count} часов
 * @param count - количество секунд, которое необходимо прибавить к {date}
 */
export const addSecondsToDate = (date: FlexDate, count: number) => {
  return dayjs(date).add(count, 'seconds').toDate();
};

/**
 * Возвращает дату в будущем
 * @param date - дата к которой нужно добавить {duration} времени
 * @param duration - длительность в ISO 8601
 */
export const addDurationToDate = (date: FlexDate, duration: string) => {
  return dayjs(date).add(dayjs.duration(duration)).toDate();
};

export const isPastDate = (date: FlexDate, nowDate: FlexDate = new Date()) => {
  return dayjs(date).isBefore(nowDate);
};

export const isFutureDate = (date: FlexDate, nowDate: FlexDate = new Date()) => {
  return dayjs(date).isAfter(nowDate);
};

export const isZeroDate = (date: FlexDate) => {
  return dayjs(date).year() < 2;
};

/**
 * Возвращает флаг, прошло ли нужное кол-во времени с переданной даты до сейчас
 * @param duration - длительность в милисекундах, либо в ISO 8601 (PnYnMnDTnHnMnS)
 * https://day.js.org/docs/en/durations/as-iso-string
 * @example
 * прошла минута
 * timePassed('2022-01-01T00:00:00.000Z', '2022-01-02T00:00:00.000Z', 'PT1M')
 * прошла неделя
 * timePassed('2022-01-01T00:00:00.000Z', '2022-01-02T00:00:00.000Z', 'P7D')
 */
export function timePassed(targetDate: FlexDate, nowDate: FlexDate, duration: string | number) {
  return dayjs(targetDate)
    .add(dayjs.duration(duration as string))
    .isBefore(dayjs(nowDate));
}

/**
 * True если обе даты попадают в один календарный день
 * в часовом поясе устройства (локальная зона).
 *
 * Входные даты могут быть в любом формате (UTC или локальная),
 * мы трактуем их как абсолютные моменты времени (UTC), а затем
 * конвертируем в локальную зону.
 */
export function isSameCalendarDayInDeviceTz(date1: FlexDate, date2: FlexDate) {
  const localTz = dayjs.tz.guess();

  const d1 = dayjs.utc(date1).tz(localTz);
  const d2 = dayjs.utc(date2).tz(localTz);

  return d1.isSame(d2, 'day');
}

export function getMultipliedDuration(duration: string, count: number) {
  return dayjs.duration(dayjs.duration(duration).asMilliseconds() * count);
}

export function getLimitedDuration(duration: string, maxDuration: string) {
  const minMiliseconds = Math.min(
    dayjs.duration(duration).asMilliseconds(),
    dayjs.duration(maxDuration).asMilliseconds(),
  );
  return dayjs.duration(minMiliseconds);
}

export const getDateDiff = (first: FlexDate, second: FlexDate, unit: UnitTypeShort = 'ms') => {
  return dayjs(first).diff(second, unit);
};

/**
 * Получает длительность интервала в зависимости от номера показа.
 * Длительность увеличивается по геометрической прогрессии
 **/
export function getGrowingTimeInterval(params: {
  /** ISO 8601 (PnYnMnDTnHnMnS) */
  minInterval: string;
  /** ISO 8601 (PnYnMnDTnHnMnS) */
  maxInterval: string;
  currentCount: number;
  ratio: number;
}) {
  const { currentCount, minInterval, maxInterval, ratio } = params;

  const multiplier = currentCount <= 1 ? 1 : Math.pow(ratio, currentCount - 1);

  return getLimitedDuration(getMultipliedDuration(minInterval, multiplier).toISOString(), maxInterval).toISOString();
}

/**
 * Форматирует строку даты (15.11.1997) в формат бекенда (1997-11-15)
 * or 23.9 to 9-23 (incomplete date, without year)
 * @param date строка даты в формате вк (например: 5.11.1997 или 23.9)
 */

export function convertToDatingBirthDateFormat(date: string) {
  const dateComponents = date.split('.').map((item) => {
    if (Number(item) > 0) {
      return leadingZero(Number(item));
    }
  });

  return dateComponents.reverse().join('-');
}

export function formatDateToDateTime(date: FlexDate) {
  return dayjs(date).format('DD.MM.YYYY HH:mm');
}

export function formatDateToTime(date: FlexDate) {
  return dayjs(date).format('HH:mm');
}

export function formatSecondsToTimeSeconds(duration: number) {
  return dayjs.duration(duration, 's').format('mm:ss');
}

export function formatDateToDate(date: FlexDate) {
  return dayjs(date).format('DD.MM.YYYY');
}

export function customFormatDate(date: FlexDate, format: string) {
  return dayjs(date).format(format);
}

/**
 * @param duration ISO 8601
 */
export const getDaysInDuration = (duration: string) => {
  return dayjs.duration(duration).asDays();
};

export const getMsInDuration = (duration: string) => {
  return dayjs.duration(duration).asMilliseconds();
};

/**
 * @returns duration ISO 8601
 */
export const getDurationFromDays = (daysCount: number) => {
  return dayjs.duration(daysCount, 'days').toISOString();
};

export const formatDateFromNow = (date: FlexDate) => {
  return dayjs(date).fromNow();
};

export const getNextWeek = (date: FlexDate) => {
  return addDaysToDate(date, DAYS_IN_WEEK);
};

export const getNextMonth = (date: FlexDate) => {
  return addDaysToDate(date, DAYS_IN_MONTH);
};

export const getNextDay = (date: FlexDate) => {
  return addDaysToDate(date, 1);
};

export const setLocale = (localeKey: AvailableLocales) => {
  return mapLocaleToImport[localeKey]()
    .then(() => {
      dayjs.locale(localeKey);
    })
    .catch(() => null);
};
